/*
 * This module implements custom history management.
 *
 * The problematics to take into account are:
 *  - The scroll position must be saved when navigating away i.e before routing
 *  - Navigation must be handled by the router to handle redirections and
 *    using native browser methods for navigating.
 *  - The scroll position must be restored when navigation is complete i.e when
 *    the component is fully mounted.
 *
 * The flow goes as follows:
 *
 * 1. The user navigates to a new route A:
 *   - leave() is called to capture the scrolling positions of the current route.
 *   - The router routes to the new route.
 *   - enter() is called to add the new route to the history.
 *   - The history is [ X -> Y -> A* ]
 * 2. The user clicks the Back button:
 *   - leave() is called to capture the scrolling positions of the current route.
 *   - The previous route is retrieved from the history.
 *   - The router routes to the previous route.
 *   - enter() is called to update the history index and point to the previous route.
 *   - The scroll positions of the previous route are restored.
 *   - The history is [ X -> Y* -> A ]
 * 3. The user clicks Back once more.
 *   - idem
 *   - The history is [ X* -> Y -> A ]
 * 4. The user clicks Forward.
 *   - leave() is called to capture the scrolling positions of the current route.
 *   - The next route is retrieved from the history.
 *   - The router routes to the next route.
 *   - enter() is called to update the history index and point to the next route.
 *   - The scroll positions of the next route are restored.
 *   - The history is [ X -> Y* -> A ]
 * 5. The user navigates to a new route B:
 *   - leave() is called to capture the scrolling positions of the current route.
 *   - The router routes to the new route.
 *   - enter() is called to add the new route to the history.
 *   - The forward routes are purged to make them unavailable to navigate forward.
 *   - The history is [ X -> Y -> B* ]
 */

import type { RouteLocationNormalized } from "vue-router";

import type { Scrolling } from "@/utils/scrolling";

/*
 * Types
 */

namespace History {
  export type Entry = {
    path: string;
    route: RouteLocationNormalized;
    positions: Scrolling.Position[] | null;
  };

  // Indicates the method of navigation last used.
  //
  // The method must be saved between leave() (taking place before routing)
  // and enter() (taking place after routing) because  the router does not
  // provide a way to know the method of navigation.
  export type Method = "new" | "backward" | "forward";
}

/*
 * Constants
 */

const History_: History.Entry[] = [];
let Index_: number | null = null;
let Method_: History.Method = "new";

/*
 * Private functions
 */

const routePath = (route: RouteLocationNormalized): string => {
  if (typeof route === "string") {
    throw new Error("Route is a string, not a route object", { cause: route });
  }
  if (!route.path) {
    throw new Error("Route has no path", { cause: route });
  }
  return route.path;
};

/*
 * Public functions
 */

export const history = {
  // Leave a route, updating the history to record the scrolling positions and method
  // of navigation.
  leave: (
    route: RouteLocationNormalized,
    positions: Scrolling.Position[],
    method: History.Method
  ) => {
    const path = routePath(route);

    // Record the method of navigation: new, forward or backward.
    //
    // This will be used when entering a new route.
    Method_ = method;

    if (Index_ !== null && Index_ >= 0 && Index_ < History_.length) {
      const entry = History_[Index_];
      if (entry.path !== path) {
        window.console.warn(
          `[history] route mismatch in leave(): ${path} vs current: ${entry.path}`
        );
        return;
      }

      entry.positions = positions;
    } else {
      window.console.warn(`[history] no entry found at index: ${Index_}`);
    }
  },

  // Enter a new route, adding it to the history.
  //
  // Since we are just navigating to this new route, the scrolling positions is set to null.
  enter: (route: RouteLocationNormalized) => {
    const path = routePath(route);

    switch (Method_) {
      case "new": {
        // If the user had gone back in the history (index < length - 1) before entering a new
        // route, then we need to purge all the forward entries to invalidate them since this
        // would not make any sense.
        if (Index_ !== null && Index_ < History_.length - 1) {
          History_.splice(Index_ + 1);
        }

        // Add the new route to the end of the history; Index_ is set to the very end of the history.
        const entry: History.Entry = {
          path,
          route,
          positions: null,
        };
        History_.push(entry);
        Index_ = History_.length - 1;
        break;
      }
      case "backward": {
        // If the user is going backward, just update the index.
        if (Index_ !== null && Index_ > 0 && Index_ < History_.length) {
          Index_ = Index_ - 1;
        }
        break;
      }
      case "forward": {
        // If the user is going forward, just update the index.
        if (Index_ !== null && Index_ < History_.length - 1) {
          Index_ = Index_ + 1;
        }
        break;
      }
      default: {
        throw new Error("Invalid method of navigation", { cause: Method_ });
      }
    }
  },

  // Return the current history entry.
  current: (): History.Entry | null => {
    if (Index_ !== null && Index_ >= 0 && Index_ < History_.length) {
      return History_[Index_];
    }
    return null;
  },

  // Return the previous history entry.
  previous: (): History.Entry | null => {
    if (Index_ !== null && Index_ >= 0 && Index_ < History_.length) {
      return History_[Index_ - 1];
    } else {
      return null;
    }
  },

  // Return the next history entry.
  next: (): History.Entry | null => {
    if (Index_ !== null && Index_ >= 0 && Index_ < History_.length - 1) {
      return History_[Index_ + 1];
    } else {
      return null;
    }
  },

  // Return the last history entry.
  last: (): History.Entry | null => {
    if (Index_ !== null && Index_ >= 0 && Index_ < History_.length) {
      return History_[History_.length - 1];
    } else {
      return null;
    }
  },

  // Check if can go back in history.
  canNavigateBackward: (): boolean => {
    return Index_ !== null && Index_ > 0 && Index_ < History_.length;
  },

  // Check if can go forward in history.
  canNavigateForward: (): boolean => {
    return Index_ !== null && Index_ >= 0 && Index_ < History_.length - 1;
  },

  // Dump the history to the console.
  dump: () => {
    // Create a safe clone that only includes serializable data.
    const _history = History_.map((entry) => ({
      path: entry.path,
      route:
        typeof entry.route === "string"
          ? entry.route
          : {
              path: entry.route.path,
              name: "name" in entry.route ? entry.route.name : undefined,
              params: "params" in entry.route ? entry.route.params : undefined,
              query: "query" in entry.route ? entry.route.query : undefined,
              hash: "hash" in entry.route ? entry.route.hash : undefined,
              // Exclude component instances and other non-serializable properties
            },
      positions: entry.positions,
    }));
    window.console.group(`[history] index=${Index_} length=${_history.length}`);
    window.console.table(
      _history.map((entry, index) => ({
        path: entry.path,
        current: index === Index_ ? "👉" : "",
        positions:
          entry.positions?.length && entry.positions.length > 0
            ? entry.positions
            : null,
      }))
    );
    window.console.groupEnd();
    return _history;
  },
};
